import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PublicNav, Footer } from "@/components/PublicLayout";

const SERVICES = ["Commercial & Heavy-Duty Maintenance", "Passenger Vehicle", "Custom Build (Boat, ATV, UTV, Lawn)", "Other"];

export default function RequestQuote() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service_type: SERVICES[0], vehicle_info: "", message: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/quote-requests", form);
      setSent(true);
      toast.success("Quote request sent! We'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to send request");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-zinc-900 border border-zinc-800 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 transition-shadow duration-150";

  return (
    <div className="bg-[#09090b] min-h-screen">
      <PublicNav />
      <div className="max-w-3xl mx-auto px-6 py-20">
        <p className="text-xs font-bold tracking-[0.2em] uppercase text-yellow-500 mb-2">Get started</p>
        <h1 className="font-display text-4xl lg:text-5xl font-black uppercase tracking-tighter mb-8">Request a Quote</h1>

        {sent ? (
          <div className="border border-yellow-500/40 bg-zinc-950 p-10 text-center" data-testid="quote-success">
            <CheckCircle2 className="text-yellow-500 mx-auto mb-4" size={48} />
            <h2 className="font-display text-2xl font-bold mb-2">Request received</h2>
            <p className="text-zinc-400">Thanks {form.name.split(" ")[0]} — our team will reach out at {form.email} or {form.phone || "your phone"} soon.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5 border border-zinc-800 bg-zinc-950 p-8">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-zinc-400 block mb-2">Full name</label>
                <input required value={form.name} onChange={set("name")} className={inputCls} data-testid="quote-name" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-zinc-400 block mb-2">Phone</label>
                <input value={form.phone} onChange={set("phone")} className={inputCls} data-testid="quote-phone" />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-zinc-400 block mb-2">Email</label>
              <input required type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="quote-email" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-zinc-400 block mb-2">Service needed</label>
              <select value={form.service_type} onChange={set("service_type")} className={inputCls} data-testid="quote-service">
                {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-zinc-400 block mb-2">Vehicle info (year / make / model)</label>
              <input value={form.vehicle_info} onChange={set("vehicle_info")} className={inputCls} data-testid="quote-vehicle" placeholder="e.g. 2021 Freightliner Cascadia" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-zinc-400 block mb-2">What's going on?</label>
              <textarea rows={4} value={form.message} onChange={set("message")} className={inputCls} data-testid="quote-message" />
            </div>
            <button disabled={loading} data-testid="quote-submit"
              className="w-full bg-yellow-500 text-black font-bold uppercase px-8 py-4 flex items-center justify-center gap-2 hover:bg-yellow-300 transition-colors duration-150 disabled:opacity-60">
              {loading ? "Sending…" : <>Send Request <ArrowRight size={18} /></>}
            </button>
          </form>
        )}
      </div>
      <Footer />
    </div>
  );
}
